import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaTimes, FaBarcode, FaQrcode, FaPlus } from 'react-icons/fa';
import BarcodeScanner from '../BarcodeScanner';
import syncService from '../../services/syncService';
import { listProducts } from '../../actions/productActions';
import { fetchWarehouses } from '../../actions/inventoryActions';
import Loader from '../ui/Loader';
import ProductSelector from './ProductSelector';
import api from '../../utils/api';
import PartnerSelector from './PartnerSelector';

const InboundModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    product_id: '',
    warehouse_id: '',
    quantity_units: '',
    units_per_box: 6,
    entry_type: 'individual', // 'individual' | 'boxes' - 入库方式
    unit_name: '瓶', // 计量单位名称
    unit_price: '',
    partner_name: '',
    operator: '',
    box_barcodes: [],
    box_qrcodes: [],
    box_units: [],
    notes: ''
  });
  const [presetUnits] = useState(['瓶', '包', '盒', '罐', '袋', '个']);
  const [showCustomUnit, setShowCustomUnit] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanType, setScanType] = useState(''); // 'box_barcode', 'box_qrcode', 'unit_barcodes', 'unit_qrcodes'
  const [batchBarcodeInput, setBatchBarcodeInput] = useState('');

  const dispatch = useDispatch();
  const { products } = useSelector(state => state.productList);
  const { warehouses } = useSelector(state => state.warehouseList);
  const { user } = useSelector(state => state.auth);
  
  useEffect(() => {
    // 获取产品和仓库数据
    dispatch(listProducts());
    dispatch(fetchWarehouses());
  }, [dispatch]);
  
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        operator: user.full_name || user.phone_number || '未知用户'
      }));
    }
  }, [user]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleScanClick = (type) => {
    setScanType(type);
    setShowBarcodeScanner(true);
  };
  
  const addBarcodeFromInput = (type) => {
    if (!batchBarcodeInput.trim()) return;
    
    const codes = batchBarcodeInput.split(/[\n,;，；]+/).map(code => code.trim()).filter(code => code);
    
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], ...codes]
    }));
    
    setBatchBarcodeInput('');
  };

  const handleScanComplete = (result) => {
    // 处理URL格式的二维码
    const extractCodeFromUrl = (inputCode) => {
      try {
        if (inputCode.toLowerCase().startsWith('http')) {
          const url = new URL(inputCode);
          const codeFromX = url.searchParams.get('X');
          const codeFromCode = url.searchParams.get('code');
          const codeFromId = url.searchParams.get('id');
          
          if (codeFromX) return codeFromX;
          if (codeFromCode) return codeFromCode;
          if (codeFromId) return codeFromId;
          
          const pathParts = url.pathname.split('/').filter(part => part);
          if (pathParts.length > 0) {
            return pathParts[pathParts.length - 1];
          }
        }
        return inputCode;
      } catch (error) {
        console.warn('Failed to parse URL, using original code:', error);
        return inputCode;
      }
    };
    
    // 处理扫描结果，提取实际编码
    const processedResult = {
      ...result,
      barcodes: result.barcodes?.map(extractCodeFromUrl) || [],
      qrcodes: result.qrcodes?.map(extractCodeFromUrl) || [],
      boxUnits: result.boxUnits?.map(boxUnit => ({
        ...boxUnit,
        unit_barcodes: boxUnit.unit_barcodes?.map(extractCodeFromUrl) || [],
        unit_qrcodes: boxUnit.unit_qrcodes?.map(extractCodeFromUrl) || []
      })) || []
    };
    if (formData.entry_type === 'individual') {
      // 按瓶入库：检查是否为散货箱
      if (processedResult.isLooseBox) {
        // 散货箱模式：使用 boxUnits 数据，不使用箱码
        const newBoxUnits = processedResult.boxUnits || [];
        
        setFormData(prev => ({
          ...prev,
          box_barcodes: [], // 散货箱不设置箱码
          box_qrcodes: [],
          box_units: [...prev.box_units, ...newBoxUnits]
        }));
      } else {
        // 直接录入瓶码模式：将扫描结果作为瓶码存储到 boxUnits
        const newBarcodes = processedResult.barcodes || [];
        const newQrcodes = processedResult.qrcodes || [];
        
        const newBoxUnit = {
          unit_barcodes: newBarcodes,
          unit_qrcodes: newQrcodes
        };
        
        setFormData(prev => ({
          ...prev,
          box_barcodes: [], // 清空箱码
          box_qrcodes: [],
          box_units: [newBoxUnit] // 将瓶码存储到 box_units 中
        }));
      }
    } else {
      // 按箱入库：使用原有逻辑
      const maxLength = Math.max(processedResult.barcodes.length, processedResult.qrcodes.length);
      const newBarcodes = [];
      const newQrcodes = [];
      const newBoxUnits = processedResult.boxUnits || [];
      
      for (let i = 0; i < maxLength; i++) {
        newBarcodes.push(processedResult.barcodes[i] || '');
        newQrcodes.push(processedResult.qrcodes[i] || '');
      }
      
      setFormData(prev => ({
        ...prev,
        box_barcodes: [...prev.box_barcodes, ...newBarcodes.filter(code => code)],
        box_qrcodes: [...prev.box_qrcodes, ...newQrcodes.filter(code => code)],
        box_units: [...prev.box_units, ...newBoxUnits]
      }));
    }
    
    setShowBarcodeScanner(false);
  };
  
  const removeBarcodeItem = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const calculateBoxes = () => {
    if (formData.entry_type === 'boxes') {
      return parseInt(formData.quantity_units) || 0;
    } else {
      const units = parseInt(formData.quantity_units) || 0;
      return (units / (parseInt(formData.units_per_box) || 1)).toFixed(2);
    }
  };
  
  const calculateTotalPrice = () => {
    let totalUnits = 0;
    if (formData.entry_type === 'boxes') {
      totalUnits = (parseInt(formData.quantity_units) || 0) * (parseInt(formData.units_per_box) || 1);
    } else {
      totalUnits = parseInt(formData.quantity_units) || 0;
    }
    const price = parseFloat(formData.unit_price) || 0;
    return (totalUnits * price).toFixed(2);
  };
  
const handleProductSelect = (productId) => {
  setFormData(prev => ({ ...prev, product_id: productId }));
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.warehouse_id) {
      setError('请选择仓库');
      return;
    }

    if (formData.entry_type === 'boxes') {
      const expectedBoxes = parseInt(formData.quantity_units) || 0;
      const actualBarcodes = formData.box_barcodes.length;
      
      if (actualBarcodes > 0 && actualBarcodes !== expectedBoxes) {
        setError(`箱数(${expectedBoxes})与条码数量(${actualBarcodes})不匹配，请检查！`);
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const allUnitBarcodes = [];
      const allUnitQrcodes = [];

      if (formData.entry_type === 'boxes') {
        formData.box_units.forEach(boxUnit => {
          if (boxUnit.unit_barcodes) {
            allUnitBarcodes.push(...boxUnit.unit_barcodes);
          }
          if (boxUnit.unit_qrcodes) {
            allUnitQrcodes.push(...boxUnit.unit_qrcodes);
          }
        });
      }
      const inboundData = {
        type: 'inbound',
        product_id: parseInt(formData.product_id),
        warehouse_id: parseInt(formData.warehouse_id),
        total_units: formData.entry_type === 'boxes' 
          ? (parseInt(formData.quantity_units) || 0) * (parseInt(formData.units_per_box) || 1)
          : parseInt(formData.quantity_units),
        units_per_box: formData.entry_type === 'boxes' ? parseInt(formData.units_per_box) : 1,
        unit_cost: parseFloat(formData.unit_price),
        supplier: formData.partner_name || '',
        operator: formData.operator || '',
        notes: formData.notes || '',
        boxes: []
      };

      // 构建箱数据
      const totalUnits = inboundData.total_units;
      const unitsPerBox = inboundData.units_per_box;

      // 如果是按瓶入库且没有箱码，创建一个散货箱
      const totalBoxes = formData.entry_type === 'individual' && 
                        formData.box_barcodes.length === 0 && 
                        formData.box_qrcodes.length === 0 
                        ? 1 
                        : Math.ceil(totalUnits / unitsPerBox);


      for (let i = 0; i < totalBoxes; i++) {
        const remainingUnits = totalUnits - (i * unitsPerBox);
        const unitsInThisBox = formData.entry_type === 'individual' && 
                               formData.box_barcodes.length === 0 && 
                               formData.box_qrcodes.length === 0
                               ? totalUnits  // 散货箱包含所有瓶子
                               : Math.min(unitsPerBox, remainingUnits);
        
        const boxData = {
          box_index: i + 1,
          box_barcode: formData.entry_type === 'individual' && 
                       formData.box_barcodes.length === 0 && 
                       formData.box_qrcodes.length === 0
                       ? `LOOSE-${Date.now()}` // 散货箱自动生成箱码
                       : formData.box_barcodes[i] || null,
          box_qrcode: formData.entry_type === 'individual' && 
                      formData.box_barcodes.length === 0 && 
                      formData.box_qrcodes.length === 0
                      ? null 
                      : formData.box_qrcodes[i] || null,
          units_count: unitsInThisBox,
          units: []
        };

        // 构建瓶数据
        if (formData.entry_type === 'individual' && 
            formData.box_barcodes.length === 0 && 
            formData.box_qrcodes.length === 0) {
          // 按瓶入库且没有箱码：从 box_units 中获取瓶码数据
          const firstBoxUnit = formData.box_units[0];
          if (firstBoxUnit) {
            const unitBarcodes = firstBoxUnit.unit_barcodes || [];
            const unitQrcodes = firstBoxUnit.unit_qrcodes || [];
            const maxUnits = Math.max(unitBarcodes.length, unitQrcodes.length, unitsInThisBox);
            
            for (let j = 0; j < maxUnits; j++) {
              const unitData = {
                unit_index: j + 1,
                unit_barcode: unitBarcodes[j] || null,
                unit_qrcode: unitQrcodes[j] || null,
              };
              boxData.units.push(unitData);
            }
          } else {
            // 如果没有扫描数据，创建空的瓶记录
            for (let j = 0; j < unitsInThisBox; j++) {
              const unitData = {
                unit_index: j + 1,
                unit_barcode: null,
                unit_qrcode: null,
              };
              boxData.units.push(unitData);
            }
          }
        } else {
          // 按箱入库：使用原有逻辑
          const boxUnits = formData.box_units[i];
          for (let j = 0; j < unitsInThisBox; j++) {
            const unitData = {
              unit_index: j + 1,
              unit_barcode: boxUnits?.unit_barcodes?.[j] || null,
              unit_qrcode: boxUnits?.unit_qrcodes?.[j] || null
            };
            boxData.units.push(unitData);
          }
        }
        inboundData.boxes.push(boxData);
      }
      
      await syncService.saveTransaction(inboundData);
      
      // 显示成功消息
      const totalUnitsText = formData.entry_type === 'boxes' 
        ? `${formData.quantity_units}箱 (${inboundData.total_units}${formData.unit_name})`
        : `${formData.quantity_units}${formData.unit_name}`;
      const unitCodesText = formData.entry_type === 'boxes' && allUnitBarcodes.length > 0 
        ? `\n瓶条码: ${allUnitBarcodes.length}个`
        : '';

      alert(`入库成功！\n数量：${totalUnitsText}\n箱条码数：${formData.box_barcodes.length}个${unitCodesText}`);
      
      onSuccess();
    } catch (err) {
      setError(err.message || '保存失败');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex-shrink-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">入库登记</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 基本信息 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold mb-4">基本信息</h3>
                
                {/* 产品选择保持不变 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    产品 *
                  </label>
                  <ProductSelector
                    value={formData.product_id}
                    onChange={handleProductSelect}
                    required={true}
                    placeholder="选择或搜索产品"
                    allowCreate={true}
                    onCreateNew={async (name) => {
                      try {
                        // 创建产品数据 - 使用FormData以支持文件上传
                        const formData = new FormData();
                        formData.append('name', ''); // 英文名称可选
                        formData.append('chinese_name', name); // 使用输入的名称作为中文名称
                        formData.append('description', '');
                        formData.append('price', '1'); // 设置默认价格为1元，后续可编辑
                        // 不传 category_id，让其为空
                        
                        // 调用API创建产品
                        const response = await api.post('/products', formData, {
                          headers: {
                            'Content-Type': 'multipart/form-data'
                          }
                        });
                        
                        // 刷新产品列表
                        dispatch(listProducts());
                        
                        // 返回创建的产品对象
                        return response.data.product || response.data;
                      } catch (err) {
                        console.error('创建产品失败:', err);
                        const errorMessage = err.response?.data?.message || err.message;
                        setError('创建产品失败: ' + errorMessage);
                        return null;
                      }
                    }}
                  />
                </div>
                
                {/* 仓库选择保持不变 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    仓库 *
                  </label>
                  <select
                    name="warehouse_id"
                    value={formData.warehouse_id}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                    required
                  >
                    <option value="">选择仓库</option>
                    {warehouses?.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* 供应商和操作人保持不变 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    供应商 *
                  </label>
                  <PartnerSelector
                    value={formData.partner_name}
                    onChange={(value) => setFormData(prev => ({ ...prev, partner_name: value }))}
                    type="supplier"
                    required={true}
                    allowCreate={true}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    操作人
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.operator}
                      className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                      readOnly
                    />
                    <div className="absolute right-2 top-2">
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                        当前用户
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    操作人自动设置为当前登录的管理员，不可修改
                  </p>
                </div>
              </div>
              
              {/* 数量和价格 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold mb-4">数量和价格</h3>
                
                {/* 计量单位 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    计量单位 *
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="unit_name"
                      value={showCustomUnit ? 'custom' : formData.unit_name}
                      onChange={(e) => {
                        if (e.target.value === 'custom') {
                          setShowCustomUnit(true);
                        } else {
                          setFormData(prev => ({ ...prev, unit_name: e.target.value }));
                          setShowCustomUnit(false);
                        }
                      }}
                      className="flex-1 p-2 border rounded-lg"
                    >
                      {presetUnits.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                      <option value="custom">自定义...</option>
                    </select>
                    {showCustomUnit && (
                      <input
                        type="text"
                        value={formData.unit_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, unit_name: e.target.value }))}
                        className="w-20 p-2 border rounded-lg"
                        placeholder="单位"
                        maxLength="2"
                      />
                    )}
                  </div>
                </div>
                
                {/* 入库方式 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    入库方式 *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, entry_type: 'individual' }))}
                      className={`p-3 rounded-lg border-2 ${
                        formData.entry_type === 'individual'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      按{formData.unit_name}入库
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, entry_type: 'boxes' }))}
                      className={`p-3 rounded-lg border-2 ${
                        formData.entry_type === 'boxes'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      按箱入库
                    </button>
                  </div>
                </div>
                
                {/* 数量输入 */}
                <div className="grid grid-cols-2 gap-4">
                  {formData.entry_type === 'boxes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        每箱数量 *
                      </label>
                      <input
                        type="number"
                        name="units_per_box"
                        value={formData.units_per_box}
                        onChange={handleChange}
                        className="w-full p-2 border rounded-lg"
                        min="1"
                        required
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        每箱{formData.unit_name}数
                      </div>
                    </div>
                  )}
                  
                  <div className={formData.entry_type === 'individual' ? 'col-span-2' : ''}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      入库数量 *
                    </label>
                    <input
                      type="number"
                      name="quantity_units"
                      value={formData.quantity_units}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg"
                      min="1"
                      required
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {formData.entry_type === 'boxes' ? '箱数' : `${formData.unit_name}数`}
                    </div>
                  </div>
                </div>
                
                {/* 总计显示 */}
                <div className="bg-gray-100 p-3 rounded">
                  <p className="text-sm">
                    {formData.entry_type === 'boxes' ? (
                      <>
                        共计: <span className="font-bold">{formData.quantity_units} 箱</span>
                        <span className="text-gray-600 ml-2">
                          ({(parseInt(formData.quantity_units || 0) * parseInt(formData.units_per_box || 1))} {formData.unit_name})
                        </span>
                      </>
                    ) : (
                      <>
                        共计: <span className="font-bold">{formData.quantity_units} {formData.unit_name}</span>
                      </>
                    )}
                  </p>
                </div>
                
                {/* 价格输入 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      进货单价(元/{formData.unit_name}) *
                    </label>
                    <input
                      type="number"
                      name="unit_price"
                      value={formData.unit_price}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      总价(元)
                    </label>
                    <input
                      type="text"
                      value={`¥ ${calculateTotalPrice()}`}
                      className="w-full p-2 border rounded-lg bg-gray-100"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* 条码信息 */}
            <div className="space-y-4 mt-8">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">
                  {formData.entry_type === 'individual' ? `${formData.unit_name}码信息` : '箱条码信息'}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowBarcodeScanner(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <FaBarcode className="mr-2" />
                  {formData.entry_type === 'individual' ? `扫描/录入${formData.unit_name}码` : '扫描/录入箱码'}
                </button>
              </div>
              
              {/* 箱码/瓶码显示 */}
              {(formData.box_barcodes.length > 0 || formData.box_qrcodes.length > 0 || formData.box_units.length > 0) && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-700 mb-3">
                    {formData.entry_type === 'individual' ? `${formData.unit_name}码记录` : '箱条码记录'}
                  </h4>
                  
                  {/* 散货入库：显示瓶码数据 */}
                  {formData.entry_type === 'individual' && formData.box_units.length > 0 && 
                  formData.box_barcodes.length === 0 && formData.box_qrcodes.length === 0 ? (
                    <div className="space-y-3">
                      {formData.box_units.map((boxUnit, boxIndex) => (
                        <div key={boxIndex} className="bg-white p-3 rounded border">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm font-medium text-gray-600">
                              📋 散货箱 - 已扫描{formData.unit_name}码
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  box_units: prev.box_units.filter((_, i) => i !== boxIndex)
                                }));
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>
                          
                          {/* 显示瓶码列表 */}
                          {boxUnit.unit_barcodes && boxUnit.unit_barcodes.length > 0 && (
                            <div className="mb-2">
                              <div className="text-xs text-gray-500 mb-1">瓶条码:</div>
                              <div className="flex flex-wrap gap-1">
                                {boxUnit.unit_barcodes.map((barcode, index) => (
                                  <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                                    <FaBarcode className="inline mr-1" size={10} />
                                    {barcode.slice(-8)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {boxUnit.unit_qrcodes && boxUnit.unit_qrcodes.length > 0 && (
                            <div>
                              <div className="text-xs text-gray-500 mb-1">瓶二维码:</div>
                              <div className="flex flex-wrap gap-1">
                                {boxUnit.unit_qrcodes.map((qrcode, index) => (
                                  <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-mono">
                                    <FaQrcode className="inline mr-1" size={10} />
                                    {qrcode.slice(-8)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-500 mt-2">
                            总计: {(boxUnit.unit_barcodes?.length || 0) + (boxUnit.unit_qrcodes?.length || 0)} 个{formData.unit_name}码
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* 原有的箱码显示逻辑 */
                    <div className="space-y-3">
                      {Array.from({ length: Math.max(formData.box_barcodes.length, formData.box_qrcodes.length) }, (_, index) => (
                        <div key={index} className="bg-white p-3 rounded border">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm font-medium text-gray-600">
                              第 {index + 1} {formData.entry_type === 'individual' ? formData.unit_name : '箱'}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const newBarcodes = [...formData.box_barcodes];
                                const newQrcodes = [...formData.box_qrcodes];
                                newBarcodes.splice(index, 1);
                                newQrcodes.splice(index, 1);
                                setFormData(prev => ({
                                  ...prev,
                                  box_barcodes: newBarcodes,
                                  box_qrcodes: newQrcodes
                                }));
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>
                          
                          {formData.box_barcodes[index] && (
                            <div className="flex items-center text-sm mb-1">
                              <FaBarcode className="text-blue-500 mr-2" size={14} />
                              <span className="font-mono">{formData.box_barcodes[index]}</span>
                            </div>
                          )}
                          
                          {formData.box_qrcodes[index] && (
                            <div className="flex items-center text-sm">
                              <FaQrcode className="text-green-500 mr-2" size={14} />
                              <span className="font-mono">{formData.box_qrcodes[index]}</span>
                            </div>
                          )}
                          
                          {/* 显示该箱的瓶码信息 */}
                          {formData.box_units[index] && (
                            <div className="mt-2 pt-2 border-t">
                              <div className="text-xs text-gray-500 mb-1">瓶码信息:</div>
                              {formData.box_units[index].unit_barcodes?.length > 0 && (
                                <div className="text-xs text-blue-600">
                                  瓶条码: {formData.box_units[index].unit_barcodes.length} 个
                                </div>
                              )}
                              {formData.box_units[index].unit_qrcodes?.length > 0 && (
                                <div className="text-xs text-green-600">
                                  瓶二维码: {formData.box_units[index].unit_qrcodes.length} 个
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {formData.box_barcodes.length === 0 && formData.box_qrcodes.length === 0 && formData.box_units.length === 0 && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <FaBarcode className="text-4xl text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">点击上方按钮开始扫描或录入条码信息</p>
                </div>
              )}
            </div>
            
            {/* 备注 */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                备注
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </form>
            
            {/* 固定底部按钮 */}
            <div className="flex-shrink-0 border-t bg-white px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {loading ? <Loader size="sm" /> : '确认入库'}
              </button>
            </div>
        </div>
      </div>

      {/* 条码扫描器 */}
      {showBarcodeScanner && (
        <BarcodeScanner
          onComplete={handleScanComplete}
          onClose={() => setShowBarcodeScanner(false)}
          expectedCount={parseInt(formData.quantity_units) || 1}
          title={formData.entry_type === 'individual' ? 
            `${formData.unit_name}条码/二维码录入` : 
            "箱条码/二维码录入"}
          isUnitInbound={formData.entry_type === 'individual'}
          unitName={formData.unit_name}
        />
      )}
    </div>
  );
};

export default InboundModal;